import { BillRepository } from '~/repositories/billRepository';
import { isBillPaymentValid } from './helpers';
import type {
  GetVendorBillsToSettleParams,
  SettleVendorBillPaymentDto,
  SettleVendorBillsDto,
  SettleVendorBillsGroupDto,
} from '~/swagger/portal/Api';
import type { BillPaymentSection, PayVendorBillsStoreData } from '~/types/payVendorBills';

const billRepo = new BillRepository();

function buildSettleVendorBillsPayload(storeData: PayVendorBillsStoreData): SettleVendorBillsDto {
  const filteredBillPayments = storeData.billPayments.filter(isBillPaymentValid);

  const findCommonData = (section: BillPaymentSection) => {
    const firstBill = filteredBillPayments.find((b) => b.vendor.id === section.vendorId && section.billIds.includes(b.id));
    if (!firstBill) return undefined;

    const bankAccount = storeData.bankAccounts.find((a) => a.id === firstBill.paymentBankAccountId);
    return {
      vendorId: section.vendorId,
      bankAccountId: firstBill.paymentBankAccountId!,
      cashChartOfAccountId: bankAccount?.chartOfAccountIds?.[0] ?? '',
      paymentType: firstBill.paymentType,
      paymentDate: storeData.paymentDate,
      externalTransactionId: firstBill.externalTransactionId,
      paymentMethod: firstBill.paymentMethod,
      paymentMethodId: firstBill.receivingBankAccountId,
      memo: firstBill.memo,
    };
  };

  const groups: SettleVendorBillsGroupDto[] = [];

  for (const section of storeData.billPaymentSections) {
    const commonData = findCommonData(section);
    const payments = section.billIds
      .map((billId) => {
        const data = filteredBillPayments.find((b) => b.id === billId && b.vendor.id === section.vendorId);
        return data ? { vendorBillId: billId, amount: data.paymentAmount! } : null;
      })
      .filter((p): p is SettleVendorBillPaymentDto => p !== null);

    if (commonData && payments.length) {
      groups.push({ ...commonData, payments });
    }
  }

  return { groups };
}

export function useVendorBillsPay() {
  const fetchBills = (filter?: GetVendorBillsToSettleParams) => billRepo.getVendorBillsSettle(filter);

  const payBills = async (storeData: PayVendorBillsStoreData) => {
    const payload = buildSettleVendorBillsPayload(storeData);
    return billRepo.settleVendorBills(payload);
  };

  return { fetchBills, payBills };
}
